import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import * as Permissions from "../../supportFunctions/Permissions";
import ChangePassword from "../UserManagement/ChangePassword";
import ModifyProfileForm from "./ModifyProfileForm";
import { PageHeader } from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import VerticalTabs from "../../sharedComponents/VerticalTabs";
import UserPreferencesForm from "../UserManagement/UserPreferencesForm";
import { useGetUserProfile } from "../../../hooks/useGetUserProfile";
import useProfileMutation, {
  ProfileActions,
} from "../../../hooks/useProfileMutation";

interface Props {
  editPermission: string[];
  manageUserPreferencesPermission: string[];
}

const MyProfile: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const history = useHistory();

  const location = useLocation();

  const { profile, loading, refetch } = useGetUserProfile(UsersService.user.id);

  const { mutations } = useProfileMutation();

  const canManageUserPreferences = Permissions.IsAllowed(
    props.manageUserPreferencesPermission,
    UsersService.permissions
  );

  const onSaveDetails = (values: any) => {
    mutations
      .mutate(ProfileActions.DETAILS, values, UsersService.user.id)
      .then(() => refetch());
  };

  const onSavePreferences = (values: any) => {
    mutations
      .mutate(ProfileActions.PREFERENCES, values, UsersService.user.id)
      .then(() => history.go(0));
  };

  const onSavePassword = (values: any) => {
    mutations.mutate(ProfileActions.PASSWORD, values, UsersService.user.id);
  };

  const getTabs = () => {
    const tabs = [
      {
        title: t("profile_Personal_Details_Tab"),
        content: (
          <ModifyProfileForm
            profile={profile}
            onSave={onSaveDetails}
            isLoading={loading}
          />
        ),
      },
      {
        title: t("profile_Change_Password"),
        path: "/changePassword",
        content: <ChangePassword onSave={onSavePassword} />,
      },
    ];

    if (canManageUserPreferences) {
      const userPreferencesTab = {
        title: t("interface_preferences"),
        path: "/preferences",
        content: (
          <UserPreferencesForm
            userPreferences={profile.userPreferences}
            isLoading={loading}
            onSave={onSavePreferences}
          />
        ),
      };
      tabs.splice(1, 0, userPreferencesTab);
    }
    return tabs;
  };

  const canSubmitForm = () => {
    if (
      location.pathname === "/profile" &&
      !UsersService.permissions.canEditPersons
    ) {
      return false;
    }

    return true;
  };

  const redirectToHomePage = () => history.push("/");

  return (
    <>
      <PageHeader
        title={t("profile_My_Profile_Tab")}
        navigationTree={[
          { text: `Accelerate ${t("universal_Home")}`, href: "/" },
          { text: t("universal_Profile"), href: "" },
        ]}
        secondaryButton={{
          label: t("universal_Cancel"),
          id: "cancel",
          type: "button",
          onClick: redirectToHomePage,
        }}
        primaryButton={{
          label: t("universal_Save"),
          type: "submit",
          form: "modify-profile-form",
          visible: canSubmitForm(),
          loading: mutations.isLoading,
        }}
      />
      <VerticalTabs tabs={getTabs()} />
    </>
  );
};

export default MyProfile;
